/**
 * API Key Settings Component
 *
 * Displays API key management for integration access.
 */

import { useState, useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { subscriptionApi } from '@services/api/subscription';
import ApiKeyDisplay from '@components/subscription/ApiKeyDisplay';
import type { Subscription } from '@types';

export function ApiKeySettings() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await subscriptionApi.getCurrentSubscription();
      setSubscription(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load subscription data'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  const handleRegenerate = () => {
    fetchSubscription();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading API key
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={fetchSubscription}
                className="rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <ApiKeyDisplay
        apiKey={subscription?.api_key}
        onRegenerate={handleRegenerate}
      />
    </div>
  );
}

export default ApiKeySettings;
